import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, IconButton } from '@kontentino/ui';
import { faCopy } from '@fortawesome/pro-regular-svg-icons/faCopy';
import { copyTextToClipboard } from '@kontentino/kontentino-utils/clipboard';
import { showToast } from 'app/hooks/useToast';

const COPIED_LABEL_DURATION_MS = 3000;

interface ActivityLogPostIdProps {
  postId: number;
}

const ActivityLogPostId = ({ postId }: ActivityLogPostIdProps) => {
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);
  const copiedResetTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(
    () => () => {
      if (copiedResetTimeoutRef.current) {
        clearTimeout(copiedResetTimeoutRef.current);
      }
    },
    [],
  );

  const handleCopy = async () => {
    try {
      await copyTextToClipboard(`${postId}`);
    } catch {
      showToast(t('somethingWentWrong'), 'error');
      return;
    }

    setIsCopied(true);

    if (copiedResetTimeoutRef.current) {
      clearTimeout(copiedResetTimeoutRef.current);
    }

    copiedResetTimeoutRef.current = setTimeout(
      () => setIsCopied(false),
      COPIED_LABEL_DURATION_MS,
    );
  };

  return (
    <div
      className="tw-flex tw-h-9 tw-shrink-0 tw-items-center tw-gap-1 tw-rounded-md tw-bg-grayscale-5 tw-px-2 tw-text-xs"
      data-name="activity-log-post-id"
      data-cy="activity-log-post-id"
    >
      <span className="tw-text-grayscale-100">Post ID:</span>
      <span className="tw-font-medium tw-text-grayscale-180">{postId}</span>
      {isCopied ? (
        <span
          className="tw-ml-auto tw-shrink-0 tw-font-medium tw-text-success-100"
          data-name="activity-log-post-id-copied"
          data-cy="activity-log-post-id-copied"
        >
          Copied!
        </span>
      ) : (
        <IconButton
          variant="plain"
          size="small"
          className="tw-ml-auto tw-shrink-0"
          icon={<Icon icon={faCopy} />}
          aria-label={t('copy')}
          data-name="activity-log-post-id-copy"
          data-cy="activity-log-post-id-copy"
          onClick={handleCopy}
        />
      )}
    </div>
  );
};

export default ActivityLogPostId;
